"use client";
import { DirectionsRenderer, GoogleMap } from "@react-google-maps/api";
import { useSocket } from "Context/SocketContext";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import styles from "./map.module.scss";
import { ROUTE_STOP_IMAGES } from "../../constants";
import { RouteEmergencyService, RouteEmergencyStop } from "Data/interfaces";

type LocationType = {
  id?: number,
  lat: number;
  lng: number;
  status?: string,
};

type MarkerPositionType = {
  route_id?: number;
  lat: number;
  lng: number;
}

type ServiceMarkerType = {
  active_stop_id: number;
  latitude: string | number;
  longitude: string | number;
  type: string
}

type StopMarkerType = {
  active_stop_id: number;
  latitude: string | number;
  longitude: string | number;
  type: string;
  notes?: string | null;
  image_url?: string | null
}

type UpdateMarkerType = {
  active_route_id: string | number;
  route_stop_id: string | number;
  status: string;
}

interface MapContainerProps {
  origin: LocationType;
  destination: LocationType;
  waypoints?: LocationType[];
  emergency_service?: RouteEmergencyService[];
  emergency_stop?: RouteEmergencyStop[];
  hasMarker?: boolean;
  route_id?: number;
  option?: Partial<object>;
  className?: string;
  zoom?: number;
}

const MapDirection: React.FC<MapContainerProps> = ({
  origin,
  destination,
  waypoints = [],
  emergency_service = [],
  emergency_stop = [],
  hasMarker = false,
  route_id = undefined,
  option = {},
  className = "",
  zoom = 9,
}) => {
  const socket = useSocket();
  const { isSocketConnected } = useSelector((state: RootState) => state.auth)
  const mapRef = useRef<google.maps.Map | null>(null);
  const [direction, setDirection] =
    useState<google.maps.DirectionsResult | null>(null);
  const options = useMemo(
    () => ({
      mapId: "17c975ad78fa8a12",
      disableDefaultUI: true,
      clickableIcons: false,
      gestureHandling: "auto",
      draggable: true,
      scaleControl: true,
      zoomControl: true,
      ...option,
    }),
    []
  );

  const markersDataRef = useRef<LocationType[]>([origin, ...waypoints, destination]);
  const markersRef = useRef<google.maps.Marker[] | null>(null);
  const vehicleMarkerRef = useRef<google.maps.Marker | null>(null);
  const serviceMarkerRef = useRef<google.maps.Marker[]>([]);
  const stopMarkerRef = useRef<google.maps.Marker[]>([]);


  useEffect(() => {

    if (hasMarker && isSocketConnected) {
      socket?.on('receivedLocation', (data: MarkerPositionType) => {
        if (data.lat && data.lng) {
          if (vehicleMarkerRef.current) {
            // Move marker without triggering re-render
            vehicleMarkerRef.current.setPosition(new google.maps.LatLng(data.lat, data.lng));
          } else {
            // Create the marker only once
            vehicleMarkerRef.current = new google.maps.Marker({
              position: new google.maps.LatLng(data.lat, data.lng),
              map: mapRef.current!,
              icon: {
                url: "/images/map-marker.png", // Replace with your custom icon path
                scaledSize: new google.maps.Size(60, 60), // Adjust size as needed
                anchor: new google.maps.Point(20, 20), // Adjust anchor to center
              }
            });

            if (mapRef.current) {
              mapRef.current.panTo({ lat: data.lat, lng: data.lng });
            }
          }
        }
      })

      socket?.on('routeStatus', (data: UpdateMarkerType) => {
        updateMarkerStatus(Number(data.route_stop_id), data.status)
      })

      socket?.on('emergencyService', (data: ServiceMarkerType) => {
        const marker = getDefaultMarker(Number(data.latitude), Number(data.longitude))
        serviceMarkerRef.current = [...serviceMarkerRef.current, marker]
      })

      socket?.on('emergencyStops', (data: StopMarkerType) => {
        const marker = getDefaultMarker(Number(data.latitude), Number(data.longitude))
        stopMarkerRef.current = [...stopMarkerRef.current, marker]
      })

      socket?.emit('joinRoute', { route_id: route_id })
    }

    return () => {
      socket?.dispose('receivedLocation')
      socket?.dispose('routeStatus')
      socket?.dispose('emergencyService')
      socket?.dispose('emergencyStops')
    }
  }, [isSocketConnected, mapRef.current])

  useEffect(() => {
    if (!mapRef.current) return;


    const directions: LocationType[] = [origin, ...waypoints, destination]
    // Add custom origin marker

    markersRef.current = directions.map((item, index) => {

      if (markersRef?.current) {
        // Remove old markers if they exist
        markersRef.current[index].setMap(null)
      }

      return new google.maps.Marker({
        position: new google.maps.LatLng(item.lat, item.lng),
        map: mapRef.current,
        icon: {
          url: item?.status ? ROUTE_STOP_IMAGES[item.status] : ROUTE_STOP_IMAGES['default'],
          scaledSize: new google.maps.Size(30, 40),
        },
        label: {
          text: `${index + 1}`, // D for Destination
          color: "white",
          fontSize: "15px",
          fontWeight: "bold",
          className: "mb-2"
        },
      });
    })


    serviceMarkerRef.current = emergency_service.map((item, index) => {
      if (serviceMarkerRef?.current?.length > index) {
        // Remove old markers if they exist
        serviceMarkerRef.current[index]?.setMap(null)
      }
      return getDefaultMarker(Number(item.latitude), Number(item.longitude))
    })

    stopMarkerRef.current = emergency_stop.map((item, index) => {
      if (stopMarkerRef?.current?.length > index) {
        // Remove old markers if they exist
        stopMarkerRef.current[index]?.setMap(null)
      }
      return getDefaultMarker(Number(item.latitude), Number(item.longitude))
    })


    return () => {
      if (markersRef.current) {
        markersRef.current?.forEach(item => item?.setMap(null));
        vehicleMarkerRef.current?.setMap(null)
        serviceMarkerRef.current?.forEach(item => item?.setMap(null));
        stopMarkerRef.current?.forEach(item => item?.setMap(null));
      }
    };
  }, [mapRef.current]);



  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    const directionsService = new google.maps.DirectionsService();
    const travelMode = google.maps.TravelMode.DRIVING;
    directionsService.route(
      {
        origin,
        destination,
        travelMode,
        waypoints: waypoints?.map(item => ({ location: { lat: item?.lat, lng: item?.lng }, stopover: true }))
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK) {
          setDirection(result);
        } else {
          console.error("Directions request failed due to " + status);
        }
      }
    );
  }, []);



  function getDefaultMarker(lat: number, lng: number, icon: string = ROUTE_STOP_IMAGES['default']) {
    return new google.maps.Marker({
      position: new google.maps.LatLng(lat, lng),
      map: mapRef.current!,
      icon: {
        url: icon, // Replace with your custom icon path
        scaledSize: new google.maps.Size(20, 30), // Adjust size as needed
        anchor: new google.maps.Point(20, 20), // Adjust anchor to center
      }
    });
  }


  function updateMarkerStatus(stop_id: number, status: string) {
    markersDataRef.current.forEach((item, index) => {

      if (!markersRef?.current || (item.id != stop_id)) return

      markersRef.current[index].setMap(null)
      markersDataRef.current[index].status = status

      new google.maps.Marker({
        position: new google.maps.LatLng(item.lat, item.lng),
        map: mapRef.current,
        icon: {
          url: ROUTE_STOP_IMAGES[status] ? ROUTE_STOP_IMAGES[status] : ROUTE_STOP_IMAGES['default'],
          scaledSize: new google.maps.Size(30, 40),
        },
        label: {
          text: `${index + 1}`, // D for Destination
          color: "white",
          fontSize: "15px",
          fontWeight: "bold",
          className: "mb-2"
        },
      });

      return;
    })
  }


  return (
    <div className="w-100 h-100 d-flex flex-column">
      <GoogleMap
        key={"direction-map"}
        zoom={zoom}
        center={origin}
        mapContainerClassName={`${styles.googleMap} ${className}`}
        options={options}
        onLoad={onLoad}
        mapContainerStyle={{ width: "100%", height: "100%" }}
      >
        {direction && (
          <DirectionsRenderer
            options={{
              directions: direction,
              draggable: false,
              suppressMarkers: true,
            }}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default React.memo(MapDirection);
